import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, Link } from 'react-router-dom';
import { db, collection, onSnapshot, query, orderBy, handleFirestoreError, OperationType, addDoc, updateDoc, doc, deleteDoc, Timestamp, where, createNotification, getDocs, limit, getDoc } from '../firebase';
import { Report, Task, Client, ReportType, ReportStatus, UserProfile, Account, Invoice, Branch, ReportSparePart } from '../types';
import { useAuth } from '../App';
import { 
  Plus, 
  Search, 
  FileText, 
  Download, 
  Trash2, 
  Edit2, 
  X, 
  Building2, 
  User as UserIcon,
  Calendar,
  FileDown,
  CheckCircle2,
  XCircle,
  Clock,
  Filter,
  Check,
  Loader2,
  Eye,
  Wrench,
  Receipt,
  Zap,
  Send,
  Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { toast } from 'sonner';
import { downloadFile } from '../lib/downloadHelper';
import ConfirmationModal from '../components/ConfirmationModal';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import AdvancedFilters from '../components/AdvancedFilters';
import AccountingReports from '../components/AccountingReports';
import { UserTaskReportsTab } from '../components/UserTaskReportsTab';
import { ClientBillingReport } from '../components/ClientBillingReport';
import { ProcurementHistoryReport } from '../components/ProcurementHistoryReport';

// --- PDF Generator ---
const generatePDF = (report: Report, task?: Task, letterheadUrl?: string) => {
  const doc = new jsPDF();
  const dateStr = report.createdAt?.toDate?.()?.toLocaleDateString() || new Date().toLocaleDateString();

  // --- Letterhead ---
  if (letterheadUrl) {
    try {
      doc.addImage(letterheadUrl, 'PNG', 10, 5, 190, 35);
    } catch (e) {
      console.error('Failed to add letterhead to PDF', e);
      // Fallback to default header if image fails
      doc.setFillColor(30, 64, 175);
      doc.rect(0, 0, 210, 40, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
    }
  } else {
    doc.setFillColor(30, 64, 175); // blue-800 (#1e40af)
    doc.rect(0, 0, 210, 40, 'F');
    
    // Add logo if available
    const logoUrl = '/Logo.png';
    try {
      doc.addImage(logoUrl, 'PNG', 10, 5, 30, 30);
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('BRIGHT TECHNICAL SYSTEMS', 50, 20);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(249, 115, 22); // orange-500
      doc.text('General Supplies Limited', 50, 27);
      doc.setTextColor(255, 255, 255);
      doc.text('Technical Management Solutions', 50, 34);
    } catch (e) {
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('BRIGHT TECHNICAL SYSTEMS', 20, 20);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(249, 115, 22); // orange-500
      doc.text('General Supplies Limited', 20, 27);
      doc.setTextColor(255, 255, 255);
      doc.text('Technical Management Solutions', 20, 34);
    }
    
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text('123 Tech Avenue, Innovation City', 140, 20);
    doc.text('contact@techflow.com', 140, 25);
    doc.text('+1 (555) 000-1111', 140, 30);
  }

  // --- Report Header ---
  doc.setTextColor(15, 23, 42);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(`${report.type.toUpperCase()} REPORT`, 20, 55);
  
  doc.setDrawColor(226, 232, 240); // slate-200
  doc.line(20, 60, 190, 60);

  // --- Info Table ---
  autoTable(doc, {
    startY: 65,
    head: [['Field', 'Details']],
    body: [
      ['Report ID', report.id],
      ['Date', dateStr],
      ['Client', report.clientName || 'N/A'],
      ['Technician', report.technicianName || 'N/A'],
      ['Task Title', task?.title || 'N/A'],
      ['Status', report.status.toUpperCase()],
      ['Approved By', report.managerName || 'Pending'],
    ],
    theme: 'plain',
    headStyles: { fillColor: [248, 250, 252], textColor: [100, 116, 139], fontStyle: 'bold' },
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 40 } }
  });

  let currentY = (doc as any).lastAutoTable.finalY + 10;

  // --- Equipment Table ---
  if (report.equipment && report.equipment.length > 0) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Equipment Details:', 20, currentY);
    
    autoTable(doc, {
      startY: currentY + 5,
      head: [['Equipment Name', 'Model', 'Serial Number', 'Status']],
      body: report.equipment.map(e => [e.name, e.model, e.serialNumber, e.status]),
      theme: 'striped',
      headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
      styles: { fontSize: 9 }
    });
    currentY = (doc as any).lastAutoTable.finalY + 10;
  }

  // --- Sections ---
  const sections = [
    { title: 'Findings:', content: report.findings },
    { title: 'Recommendations:', content: report.recommendations },
    { title: 'Comments:', content: report.comments }
  ];

  sections.forEach(section => {
    if (section.content) {
      if (currentY > 250) {
        doc.addPage();
        currentY = 20;
      }
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(section.title, 20, currentY);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const splitContent = doc.splitTextToSize(section.content, 170);
      doc.text(splitContent, 20, currentY + 7);
      currentY += (splitContent.length * 5) + 15;
    }
  });

  // --- Spare Parts Table ---
  if (report.requiredSpareParts && report.requiredSpareParts.length > 0) {
    if (currentY > 230) {
      doc.addPage();
      currentY = 20;
    }
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Required Spare Parts:', 20, currentY);

    autoTable(doc, {
      startY: currentY + 5,
      head: [['Part Name', 'Quantity']],
      body: report.requiredSpareParts.map(p => [p.name, p.quantity.toString()]),
      theme: 'grid',
      headStyles: { fillColor: [30, 64, 175], textColor: [255, 255, 255] },
      styles: { fontSize: 10 }
    });
    currentY = (doc as any).lastAutoTable.finalY + 10;
  }

  // --- Labour Charge ---
  if (report.labourCharge && report.labourCharge > 0) {
    if (currentY > 270) {
      doc.addPage();
      currentY = 20;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(`Estimated Labour Charge: ${report.labourCharge.toLocaleString()}`, 20, currentY);
    currentY += 15;
  }

  // --- Footer ---
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
    doc.text('This is a computer-generated report and does not require a physical signature.', 105, 290, { align: 'center' });
  }

  downloadFile(doc.output('blob'), `Report_${report.id}.pdf`);
};

// --- Fiscal Quarters & Contract Expiry ---

const getFiscalQuarters = () => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const quarters = [
    { label: `Q1 ${currentYear} (Jan-Mar)`, quarter: 1, year: currentYear, start: `${currentYear}-01-01`, end: `${currentYear}-03-31` },
    { label: `Q2 ${currentYear} (Apr-Jun)`, quarter: 2, year: currentYear, start: `${currentYear}-04-01`, end: `${currentYear}-06-30` },
    { label: `Q3 ${currentYear} (Jul-Sep)`, quarter: 3, year: currentYear, start: `${currentYear}-07-01`, end: `${currentYear}-09-30` },
    { label: `Q4 ${currentYear} (Oct-Dec)`, quarter: 4, year: currentYear, start: `${currentYear}-10-01`, end: `${currentYear}-12-31` },
    { label: `Q1 ${currentYear + 1} (Jan-Mar)`, quarter: 1, year: currentYear + 1, start: `${currentYear + 1}-01-01`, end: `${currentYear + 1}-03-31` },
    { label: `Q2 ${currentYear + 1} (Apr-Jun)`, quarter: 2, year: currentYear + 1, start: `${currentYear + 1}-04-01`, end: `${currentYear + 1}-06-30` },
    { label: `Q3 ${currentYear + 1} (Jul-Sep)`, quarter: 3, year: currentYear + 1, start: `${currentYear + 1}-07-01`, end: `${currentYear + 1}-09-30` },
    { label: `Q4 ${currentYear + 1} (Oct-Dec)`, quarter: 4, year: currentYear + 1, start: `${currentYear + 1}-10-01`, end: `${currentYear + 1}-12-31` },
  ];
  return quarters;
};

const ContractExpiryReport: React.FC<{ clients: any[] }> = ({ clients }) => {
  const quarters = getFiscalQuarters();
  const [selectedQuarterIndex, setSelectedQuarterIndex] = useState(0);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const currentQuarter = quarters[selectedQuarterIndex];
  
  const filteredClients = clients.filter(client => {
    if (!client.contractEndDate) return false;
    
    // Check if within selected quarter date range
    const expiryDate = new Date(client.contractEndDate);
    const start = new Date(currentQuarter.start);
    const end = new Date(currentQuarter.end + 'T23:59:59');
    
    const matchesQuarter = expiryDate >= start && expiryDate <= end;
    const matchesService = serviceTypeFilter === 'all' || client.serviceType === serviceTypeFilter;
    const matchesSearch = client.name.toLowerCase().includes(search.toLowerCase()) || 
                          (client.contactPerson && client.contactPerson.toLowerCase().includes(search.toLowerCase()));
                          
    return matchesQuarter && matchesService && matchesSearch;
  });

  const exportCSV = () => {
    const headers = ['Client Name', 'Contact Person', 'Email', 'Phone', 'Service Type', 'Commencement Date', 'Expiry Date', 'Duration'];
    const csvRows = [headers.join(',')];
    
    for (const c of filteredClients) {
      const values = [
        `"${c.name.replace(/"/g, '""')}"`,
        `"${(c.contactPerson || '').replace(/"/g, '""')}"`,
        `"${(c.email || '').replace(/"/g, '""')}"`,
        `"${(c.phone || '').replace(/"/g, '""')}"`,
        `"${c.serviceType || 'Not Specified'}"`,
        `"${c.contractCommencementDate || ''}"`,
        `"${c.contractEndDate || ''}"`,
        `"${c.contractDuration || ''}"`
      ];
      csvRows.push(values.join(','));
    }
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    downloadFile(blob, `Expiring_Contracts_${currentQuarter.label.replace(/\s+/g, '_')}.csv`);
    toast.success('CSV report exported successfully!');
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    
    // Header banner with corporate styling
    doc.setFillColor(30, 58, 138); // blue-900
    doc.rect(0, 0, 210, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('BRIGHT TECHNICAL SYSTEMS', 15, 18);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Contract Expiry & Renewal Planning Report', 15, 26);
    doc.text(`Fiscal Quarter: ${currentQuarter.label}`, 15, 32);
    
    doc.setTextColor(15, 23, 42);
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 15, 48);
    doc.text(`Total Expiring Contracts: ${filteredClients.length}`, 15, 54);
    
    const tableHeaders = [['Client Name', 'Contact Person', 'Service Type', 'Commencement', 'Expiry Date']];
    const tableBody = filteredClients.map(c => [
      c.name,
      c.contactPerson || 'N/A',
      c.serviceType || 'Not Specified',
      c.contractCommencementDate || 'N/A',
      c.contractEndDate || 'N/A'
    ]);
    
    autoTable(doc, {
      startY: 60,
      head: tableHeaders,
      body: tableBody,
      theme: 'striped',
      headStyles: { fillColor: [30, 58, 138] },
      styles: { fontSize: 9, cellPadding: 3 }
    });
    
    downloadFile(doc.output('blob'), `Expiring_Contracts_${currentQuarter.label.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF report exported successfully!');
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-50 border border-slate-100 p-6 rounded-3xl space-y-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-slate-900">Contract Expiry & Renewal Forecast</h3>
            <p className="text-xs text-slate-500">Filter upcoming fiscal quarters to locate and manage contracts up for renewal.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={exportCSV}
              disabled={filteredClients.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold hover:bg-emerald-50 transition-all cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV List
            </button>
            <button
              onClick={exportPDF}
              disabled={filteredClients.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-white text-slate-700 border border-slate-200 rounded-xl text-xs font-bold hover:bg-slate-50 transition-all cursor-pointer shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileDown className="w-4 h-4" />
              Export PDF Report
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Fiscal Quarter Range</label>
            <select
              value={selectedQuarterIndex}
              onChange={(e) => setSelectedQuarterIndex(parseInt(e.target.value))}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/10"
            >
              {quarters.map((q, idx) => (
                <option key={idx} value={idx}>{q.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Service Type Filter</label>
            <select
              value={serviceTypeFilter}
              onChange={(e) => setServiceTypeFilter(e.target.value)}
              className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/10"
            >
              <option value="all">All Service Types</option>
              <option value="Service">Service</option>
              <option value="Repair">Repair</option>
              <option value="Supply and Installation">Supply and Installation</option>
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Search Clients</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search Client or Person..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold focus:outline-none focus:ring-2 focus:ring-blue-800/10"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-100 rounded-3xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Client Name</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Contact Person</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Service Type</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Expiry Date</th>
                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs">
              {filteredClients.length > 0 ? (
                filteredClients.map((c) => {
                  const now = new Date();
                  const end = new Date(c.contractEndDate + 'T23:59:59');
                  const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 3600 * 24));
                  
                  return (
                    <tr key={c.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-6 py-4 font-black text-slate-800 flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-blue-900" />
                        {c.name}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-600">
                        <div>{c.contactPerson || 'N/A'}</div>
                        <div className="text-[10px] text-slate-400 font-normal">{c.email || c.phone || ''}</div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-lg uppercase">
                          {c.serviceType || 'Not Specified'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center font-mono font-bold text-slate-700">
                        {c.contractEndDate}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {daysRemaining <= 0 ? (
                          <span className="px-2 py-0.5 bg-red-50 text-red-600 text-[10px] font-black rounded uppercase">Expired</span>
                        ) : daysRemaining <= 30 ? (
                          <span className="px-2 py-0.5 bg-amber-50 text-amber-600 text-[10px] font-black rounded uppercase">Urgent ({daysRemaining}d)</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-blue-50 text-blue-800 text-[10px] font-black rounded uppercase">Expiring ({daysRemaining}d)</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-16">
                    <Calendar className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                    <p className="text-sm font-bold text-slate-400">No expiring contracts scheduled for this period.</p>
                    <p className="text-xs text-slate-400 mt-1">Try selecting a different fiscal quarter or adjusting the filters.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// --- Page Component ---

const ReportsPage: React.FC = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [managers, setManagers] = useState<UserProfile[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<{ id: string, name: string }[]>([]);
  const [allUsers, setAllUsers] = useState<UserProfile[]>([]);
  const [procurementLpos, setProcurementLpos] = useState<any[]>([]);
  const [procurementRequisitions, setProcurementRequisitions] = useState<any[]>([]);
  const [letterheadUrl, setLetterheadUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [reportLimit, setReportLimit] = useState(25);
  const [hasMoreReports, setHasMoreReports] = useState(true);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<'reports' | 'accounting' | 'contract_expiry' | 'user_tasks' | 'client_billing' | 'procurement_history'>('reports');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const clientIdParam = searchParams.get('clientId');
  const [filters, setFilters] = useState({
    type: 'all' as ReportType | 'all',
    status: 'all' as ReportStatus | 'all',
    clientId: clientIdParam || 'all',
    technicianId: 'all',
    dateFrom: '',
    dateTo: ''
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [editingReport, setEditingReport] = useState<Report | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });
  const [clientBranches, setClientBranches] = useState<Branch[]>([]);
  const [isBranchLoading, setIsBranchLoading] = useState(false);
  const [clientSpareParts, setClientSpareParts] = useState<any[]>([]);
  const [isPartSelectorOpen, setIsPartSelectorOpen] = useState(false);
  const [partSearchTerm, setPartSearchTerm] = useState('');
  const { user, profile, isAdmin, isManager, isBilling, isAccountant } = useAuth();

  const [printPreferences, setPrintPreferences] = useState<{
    printOrientation: 'portrait' | 'landscape';
    printShowHeader: boolean;
  }>({
    printOrientation: 'portrait',
    printShowHeader: true,
  });

  useEffect(() => {
    const fetchPrintPrefs = async () => {
      const userId = user?.uid || profile?.uid;
      if (!userId) return;
      try {
        const docRef = doc(db, 'userNotificationSettings', userId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setPrintPreferences({
            printOrientation: data.printOrientation || 'portrait',
            printShowHeader: data.printShowHeader !== undefined ? data.printShowHeader : true,
          });
        }
      } catch (error) {
        console.error('Error fetching print preferences:', error);
      }
    };
    fetchPrintPrefs();
  }, [user?.uid, profile?.uid]);

  // Form state
  const [formData, setFormData] = useState({
    taskId: '',
    clientId: '',
    branchId: '',
    equipment: [] as { name: string, model: string, serialNumber: string, status: string }[],
    findings: '',
    recommendations: '',
    executiveSummary: '',
    comments: '',
    type: 'general' as ReportType,
    status: 'submitted_to_technical' as ReportStatus,
    requiredSpareParts: [] as ReportSparePart[],
    labourCharge: 0
  });

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'new_survey' && !isModalOpen && !editingReport) {
      openModal();
      // Clear the param
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('action');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, isModalOpen, editingReport, setSearchParams]);

  useEffect(() => {
    if (formData.clientId) {
      setIsBranchLoading(true);
      const branchesRef = collection(db, 'clients', formData.clientId, 'branches');
      const unsubscribe = onSnapshot(branchesRef, (snapshot) => {
        const branchesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Branch));
        setClientBranches(branchesData);
        setIsBranchLoading(false);
      }, (error) => {
        handleFirestoreError(error, OperationType.LIST, `clients/${formData.clientId}/branches`);
        setIsBranchLoading(false);
      });
      return () => unsubscribe();
    } else {
      setClientBranches([]);
    }
  }, [formData.clientId]);

  useEffect(() => {
    const task = tasks.find(t => t.id === formData.taskId);
    if (task && task.clientId !== formData.clientId) {
      setFormData(prev => ({ 
        ...prev, 
        clientId: task.clientId,
        branchId: task.branchId || ''
      }));
    }
  }, [formData.taskId, tasks]);

  useEffect(() => {
    const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'), limit(reportLimit));
    const unsubscribeReports = onSnapshot(q, (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Report));
      setReports(reportsData);
      setHasMoreReports(snapshot.docs.length >= reportLimit);
      setLoading(false);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'reports');
    });

    const unsubscribeTasks = onSnapshot(collection(db, 'tasks'), (snapshot) => {
      const tasksData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Task));
      setTasks(tasksData);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'tasks');
    });

    const qManagers = query(collection(db, 'users'), where('role', 'in', ['admin', 'manager']));
    getDocs(qManagers).then(snapshot => {
      setManagers(snapshot.docs.map(doc => doc.data() as UserProfile));
    }).catch(error => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const unsubscribeClients = onSnapshot(collection(db, 'clients'), (snapshot) => {
      setClients(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'clients');
    });

    const unsubscribeUsers = onSnapshot(collection(db, 'users'), (snapshot) => {
      setAllUsers(snapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() } as UserProfile)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const unsubscribeTechs = onSnapshot(query(collection(db, 'users'), where('role', '==', 'technician')), (snapshot) => {
      setTechnicians(snapshot.docs.map(doc => ({ id: doc.id, name: doc.data().displayName })));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'users');
    });

    const unsubscribeSettings = onSnapshot(query(collection(db, 'settings'), limit(1)), (snapshot) => {
      if (!snapshot.empty) {
        setLetterheadUrl(snapshot.docs[0].data().letterheadUrl);
      }
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'settings');
    });

    const unsubscribeAccounts = onSnapshot(collection(db, 'accounts'), (snapshot) => {
      setAccounts(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Account)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'accounts');
    });

    const unsubscribeInvoices = onSnapshot(collection(db, 'invoices'), (snapshot) => {
      setInvoices(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Invoice)));
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'invoices');
    });

    const unsubscribeProcLpos = onSnapshot(collection(db, 'procurement_lpos'), (snapshot) => {
      setProcurementLpos(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Error loading LPOs for reports:', error);
    });

    const unsubscribeProcReqs = onSnapshot(collection(db, 'procurement_requisitions'), (snapshot) => {
      setProcurementRequisitions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (error) => {
      console.error('Error loading Requisitions for reports:', error);
    });

    return () => {
      unsubscribeReports();
      unsubscribeTasks();
      unsubscribeClients();
      unsubscribeUsers();
      unsubscribeTechs();
      unsubscribeSettings();
      unsubscribeAccounts();
      unsubscribeInvoices();
      unsubscribeProcLpos();
      unsubscribeProcReqs();
    };
  }, []);

  useEffect(() => {
    if (formData.clientId) {
      // Fetch client spare parts from their reports and dedicated collection
      const reportsQuery = query(collection(db, 'reports'), where('clientId', '==', formData.clientId));
      const sparePartsRef = collection(db, 'clients', formData.clientId, 'spareParts');
      
      const unsubscribeParts = onSnapshot(reportsQuery, (snapshot) => {
        const reportParts: any[] = [];
        snapshot.docs.forEach(doc => {
          const report = doc.data() as Report;
          if (report.requiredSpareParts) {
            report.requiredSpareParts.forEach(p => {
              if (!reportParts.find(existing => existing.name === p.name)) {
                reportParts.push({ ...p, id: `report-${p.name}` });
              }
            });
          }
        });
        
        // Also fetch from dedicated collection
        const unsubscribeDedicated = onSnapshot(sparePartsRef, (spareSnapshot) => {
          const dedicatedParts = spareSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as any));
          const allParts = [...reportParts];
          dedicatedParts.forEach(p => {
            if (!allParts.find(existing => (existing as any).name === p.name)) {
              allParts.push(p);
            }
          });
          setClientSpareParts(allParts);
        });

        return () => unsubscribeDedicated();
      });

      return () => unsubscribeParts();
    }
  }, [formData.clientId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const task = tasks.find(t => t.id === formData.taskId);
    if (!task) return;

    setIsSubmitting(true);
    try {
      const branch = clientBranches.find(b => b.id === formData.branchId);
      const reportData = {
        ...formData,
        clientId: task.clientId,
        clientName: task.clientName || '',
        branchId: formData.branchId || task.branchId || '',
        branchName: branch?.name || task.branchName || '',
        technicianId: profile?.uid || '',
        technicianName: profile?.displayName || '',
      };

      if (editingReport) {
        await updateDoc(doc(db, 'reports', editingReport.id), {
          ...reportData,
          updatedAt: Timestamp.now()
        });
        toast.success('Report updated successfully');
      } else {
        const reportRef = await addDoc(collection(db, 'reports'), {
          ...reportData,
          createdAt: Timestamp.now()
        });

        // Link report to task and update task status
        if (formData.taskId) {
          await updateDoc(doc(db, 'tasks', formData.taskId), {
            status: 'pending review',
            reportId: reportRef.id,
            updatedAt: Timestamp.now()
          });
        }

        // Notify managers
        for (const manager of managers) {
          await createNotification(
            manager.uid,
            'New Report Submitted',
            `${profile?.displayName} submitted a ${formData.type} report for ${task.clientName}`,
            'report_submitted',
            '/reports'
          );
        }

        // Create technical task for survey reports
        if (formData.type === 'survey') {
          await addDoc(collection(db, 'tasks'), {
            clientId: task.clientId,
            clientName: task.clientName || '',
            title: `Technical Review: ${task.clientName}`,
            description: `Technical review required for survey report submitted by ${profile?.displayName}. Findings: ${formData.findings.substring(0, 100)}...`,
            status: 'pending',
            type: 'technical',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });

          // Notify technical department users
          const qTechnical = query(collection(db, 'users'), where('role', '==', 'technical'));
          const technicalUsers = await getDocs(qTechnical);
          for (const techUser of technicalUsers.docs) {
            await createNotification(
              techUser.id,
              'New Technical Task',
              `A new technical review task has been created for ${task.clientName}`,
              'task_assigned',
              '/tasks'
            );
          }
        }
        toast.success('Report submitted successfully');
      }
      closeModal();
    } catch (error) {
      handleFirestoreError(error, editingReport ? OperationType.UPDATE : OperationType.CREATE, 'reports');
      toast.error('Failed to save report');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (report: Report, newStatus: ReportStatus) => {
    try {
      const updateData: any = { 
        status: newStatus,
        managerId: profile?.uid,
        managerName: profile?.displayName,
        approvalDate: Timestamp.now()
      };

      await updateDoc(doc(db, 'reports', report.id), updateData);
      
      // Notify technician
      await createNotification(
        report.technicianId,
        `Report ${newStatus === 'approved' ? 'Approved' : newStatus === 'rejected' ? 'Rejected' : newStatus.toUpperCase()}`,
        `Your ${report.type} report for ${report.clientName} has been ${newStatus}`,
        newStatus === 'rejected' ? 'report_rejected' : 'report_approved',
        '/reports'
      );

      // If approved and survey, create follow-up task
      if (newStatus === 'approved' && report.type === 'survey') {
        await addDoc(collection(db, 'tasks'), {
          clientId: report.clientId,
          clientName: report.clientName || '',
          branchId: report.branchId || '',
          branchName: report.branchName || '',
          title: `Follow-up: ${report.clientName} Survey`,
          description: `Follow-up task created from approved survey report.\nFindings: ${report.findings}\nRecommendations: ${report.recommendations}`,
          status: 'pending',
          type: 'technical',
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now()
        });
        toast.info('Follow-up task created automatically.');
      }

      toast.success(`Report ${newStatus} successfully`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `reports/${report.id}`);
      toast.error('Failed to update report status');
    }
  };

  const handleDelete = async (reportId: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Delete Report',
      message: 'Are you sure you want to delete this report? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'reports', reportId));
          toast.success('Report deleted successfully');
        } catch (error) {
          handleFirestoreError(error, OperationType.DELETE, `reports/${reportId}`);
          toast.error('Failed to delete report');
        }
      }
    });
  };

  const addEquipmentRow = () => {
    setFormData({
      ...formData,
      equipment: [...formData.equipment, { name: '', model: '', serialNumber: '', status: '' }]
    });
  };

  const removeEquipmentRow = (index: number) => {
    const newEquipment = [...formData.equipment];
    newEquipment.splice(index, 1);
    setFormData({ ...formData, equipment: newEquipment });
  };

  const updateEquipmentRow = (index: number, field: string, value: string) => {
    const newEquipment = [...formData.equipment];
    newEquipment[index] = { ...newEquipment[index], [field]: value };
    setFormData({ ...formData, equipment: newEquipment });
  };

  const addSparePart = async (part: any) => {
    const existing = formData.requiredSpareParts?.find(p => p.name === part.name);
    if (existing) {
      toast.error('Part already added');
      return;
    }

    // If it's a new part (from search), save it to client's spare parts
    if (part.isNew) {
      try {
        await addDoc(collection(db, 'clients', formData.clientId, 'spareParts'), {
          name: part.name,
          createdAt: Timestamp.now()
        });
      } catch (error) {
        console.error('Error saving new spare part:', error);
      }
    }

    setFormData({
      ...formData,
      requiredSpareParts: [...(formData.requiredSpareParts || []), { name: part.name, quantity: 1, unit: part.unit }]
    });
    setIsPartSelectorOpen(false);
    setPartSearchTerm('');
  };

  const removeSparePart = (index: number) => {
    const newParts = [...(formData.requiredSpareParts || [])];
    newParts.splice(index, 1);
    setFormData({ ...formData, requiredSpareParts: newParts });
  };

  const updateSparePartQty = (index: number, qty: number) => {
    const newParts = [...(formData.requiredSpareParts || [])];
    newParts[index] = { ...newParts[index], quantity: qty };
    setFormData({ ...formData, requiredSpareParts: newParts });
  };

  const openModal = (report?: Report) => {
    if (report) {
      setEditingReport(report);
      setFormData({
        taskId: report.taskId,
        clientId: report.clientId,
        branchId: report.branchId || '',
        equipment: report.equipment || [],
        findings: report.findings || '',
        recommendations: report.recommendations || '',
        executiveSummary: report.executiveSummary || '',
        comments: report.comments || '',
        type: report.type,
        status: report.status,
        requiredSpareParts: report.requiredSpareParts || [],
        labourCharge: report.labourCharge || 0
      });
    } else {
      setEditingReport(null);
      setFormData({ 
        taskId: '', 
        clientId: '',
        branchId: '',
        equipment: [], 
        findings: '', 
        recommendations: '', 
        executiveSummary: '',
        comments: '', 
        type: 'survey', 
        status: 'submitted_to_technical',
        requiredSpareParts: [],
        labourCharge: 0
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingReport(null);
  };

  const openViewModal = (report: Report) => {
    setSelectedReport(report);
    setIsViewModalOpen(true);
  };

  const closeViewModal = () => {
    setIsViewModalOpen(false);
    setSelectedReport(null);
  };

  const handlePrintSingleReport = () => {
    // Dynamically insert orientation style if needed
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `@media print { @page { size: ${printPreferences.printOrientation || 'portrait'}; margin: 15mm; } }`;
    document.head.appendChild(styleEl);

    document.body.classList.add('print-single-report-active');
    window.print();
    document.body.classList.remove('print-single-report-active');

    // Clean up
    document.head.removeChild(styleEl);
  };

  const handleDownloadPdfFromPrintableView = async (report: Report) => {
    setIsDownloadingPdf(true);
    const loadingToast = toast.loading('Generating formatted service report PDF from printable view...', { duration: 15000 });
    
    const wasAlreadyOpen = isViewModalOpen && selectedReport?.id === report.id;
    
    const preprocessStylesheets = () => {
      const restores: Array<() => void> = [];
      try {
        const styleElements = Array.from(document.querySelectorAll('style'));
        for (const styleEl of styleElements) {
          const originalText = styleEl.textContent;
          if (originalText && originalText.includes('oklch')) {
            const modifiedText = originalText.replace(/oklch\([^)]+\)/g, 'rgb(120, 120, 120)');
            styleEl.textContent = modifiedText;
            restores.push(() => {
              styleEl.textContent = originalText;
            });
          }
        }
        const linkElements = Array.from(document.querySelectorAll('link[rel="stylesheet"]')) as HTMLLinkElement[];
        for (const linkEl of linkElements) {
          try {
            const sheet = linkEl.sheet;
            if (sheet) {
              const rules = Array.from(sheet.cssRules);
              let cssText = '';
              for (const rule of rules) {
                cssText += rule.cssText + '\n';
              }
              if (cssText.includes('oklch')) {
                const modifiedText = cssText.replace(/oklch\([^)]+\)/g, 'rgb(120, 120, 120)');
                const newStyleEl = document.createElement('style');
                newStyleEl.textContent = modifiedText;
                linkEl.parentNode?.insertBefore(newStyleEl, linkEl);
                linkEl.disabled = true;
                restores.push(() => {
                  newStyleEl.parentNode?.removeChild(newStyleEl);
                  linkEl.disabled = false;
                });
              }
            }
          } catch (e) {}
        }
      } catch (err) {}
      return () => {
        for (let i = restores.length - 1; i >= 0; i--) {
          try { restores[i](); } catch (e) {}
        }
      };
    };

    let restoreStyles: (() => void) | null = null;
    
    try {
      setSelectedReport(report);
      setIsViewModalOpen(true);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const element = document.getElementById('printable-report');
      if (!element) {
        toast.dismiss(loadingToast);
        toast.error('Failed to locate printable report layout.');
        setIsDownloadingPdf(false);
        return;
      }
      
      restoreStyles = preprocessStylesheets();
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
        height: element.scrollHeight,
        windowHeight: element.scrollHeight,
      });
      
      if (restoreStyles) {
        restoreStyles();
        restoreStyles = null;
      }
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: printPreferences.printOrientation,
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const pxToMm = pdfWidth / imgWidth;
      const imgScaledHeight = imgHeight * pxToMm;
      
      if (imgScaledHeight <= pdfHeight) {
        const yOffset = (pdfHeight - imgScaledHeight) / 2;
        pdf.addImage(imgData, 'PNG', 0, yOffset, pdfWidth, imgScaledHeight);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text('Page 1 of 1', pdfWidth - 25, pdfHeight - 8);
      } else {
        const totalPages = Math.ceil(imgScaledHeight / pdfHeight);
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
          if (pageNum > 1) {
            pdf.addPage();
          }
          const yOffset = - (pageNum - 1) * pdfHeight;
          pdf.addImage(imgData, 'PNG', 0, yOffset, pdfWidth, imgScaledHeight);
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth - 25, pdfHeight - 8);
        }
      }
      
      pdf.save(`Service_Report_${report.type}_${report.id}.pdf`);
      toast.dismiss(loadingToast);
      toast.success('Formatted service report PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.dismiss(loadingToast);
      toast.error('Failed to generate PDF document.');
    } finally {
      if (restoreStyles) {
        restoreStyles();
      }
      setIsDownloadingPdf(false);
      if (!wasAlreadyOpen) {
        setIsViewModalOpen(false);
        setSelectedReport(null);
      }
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = report.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         report.technicianName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filters.type === 'all' || report.type === filters.type;
    const matchesStatus = filters.status === 'all' || report.status === filters.status;
    const matchesClient = filters.clientId === 'all' || report.clientId === filters.clientId;
    const matchesTech = filters.technicianId === 'all' || report.technicianId === filters.technicianId;
    
    let matchesDate = true;
    if (filters.dateFrom || filters.dateTo) {
      const reportDate = report.createdAt && typeof report.createdAt.toDate === 'function'
        ? report.createdAt.toDate()
        : report.createdAt instanceof Date
          ? report.createdAt
          : report.createdAt
            ? new Date(report.createdAt as any)
            : null;

      if (reportDate) {
        if (filters.dateFrom) {
          const fromDate = new Date(filters.dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (reportDate < fromDate) matchesDate = false;
        }
        if (filters.dateTo) {
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (reportDate > toDate) matchesDate = false;
        }
      } else {
        matchesDate = false;
      }
    }

    return matchesSearch && matchesType && matchesStatus && matchesClient && matchesTech && matchesDate;
  });

  const getStatusBadge = (status: ReportStatus) => {
    switch (status) {
      case 'draft': return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-bold uppercase">Draft</span>;
      case 'submitted_to_technical': return <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-[10px] font-bold uppercase">Pending Approval</span>;
      case 'approved': return <span className="px-2 py-0.5 bg-green-100 text-green-600 rounded-full text-[10px] font-bold uppercase">Approved</span>;
      case 'rejected': return <span className="px-2 py-0.5 bg-red-100 text-red-600 rounded-full text-[10px] font-bold uppercase">Rejected</span>;
      case 'submitted_to_billing': return <span className="px-2 py-0.5 bg-emerald-100 text-emerald-600 rounded-full text-[10px] font-bold uppercase">In Billing</span>;
      default: return <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full text-[10px] font-bold uppercase">{status}</span>;
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64">Loading reports...</div>;

  return (
    <div className="space-y-8">
      {/* Dedicated Print-Only Header Block */}
      <div className="print-only print-header">
        <h1 className="print-header-title">BRIGHT TECHNICAL SYSTEMS</h1>
        <p className="print-header-meta">
          <strong>Document Title:</strong> {
            activeTab === 'reports' 
              ? 'Technical Service and Maintenance Reports Summary' 
              : activeTab === 'accounting' 
                ? 'Financial ledger & Account Reports Summary' 
                : activeTab === 'contract_expiry'
                  ? 'Contract Expiry & Renewal Planning Forecast'
                  : activeTab === 'procurement_history'
                    ? 'Procurement History, Deliveries & Budget Utilization'
                    : 'User Task Summaries & Reports'
          } <br />
          <strong>Generated By:</strong> {profile?.displayName || 'Authorized User'} ({profile?.email || 'N/A'}) <br />
          <strong>Date Generated:</strong> {new Date().toLocaleString()}
        </p>
      </div>

      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-slate-500">Generate and manage technical work reports</p>
        </div>
        <div className="flex flex-wrap gap-2 bg-slate-100 p-1 rounded-2xl">
          <button onClick={() => setActiveTab('reports')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'reports' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Technical Reports</button>
          {(isAdmin || isManager || isBilling || isAccountant) && (
            <button onClick={() => setActiveTab('accounting')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'accounting' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Accounting Reports</button>
          )}
          {(isAdmin || isManager || isBilling || isAccountant) && (
            <button onClick={() => setActiveTab('contract_expiry')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'contract_expiry' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Contract Expirations</button>
          )}
          <button onClick={() => setActiveTab('user_tasks')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'user_tasks' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>User Task Reports</button>
          {(isAdmin || isManager || isBilling || isAccountant) && (
            <button onClick={() => setActiveTab('client_billing')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'client_billing' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Client Billing Statement</button>
          )}
          {(isAdmin || isManager || isBilling || isAccountant || profile?.role === 'procurement') && (
            <button onClick={() => setActiveTab('procurement_history')} className={`px-6 py-2 rounded-xl font-bold transition-all ${activeTab === 'procurement_history' ? 'bg-white text-blue-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>Procurement History</button>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Universal print trigger */}
          <button
            onClick={() => window.print()}
            className="flex items-center justify-center gap-2 bg-slate-800 text-white px-5 py-3 rounded-2xl font-bold hover:bg-slate-900 transition-all shadow-md hover:shadow-lg cursor-pointer text-sm"
            title="Print this view or export as physical document"
          >
            <Printer className="w-5 h-5" />
            Print View
          </button>
          {activeTab === 'reports' && (
            <button
              onClick={() => openModal()}
              className="flex items-center justify-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 text-sm"
            >
              <Plus className="w-5 h-5" />
              Create Report
            </button>
          )}
        </div>
      </div>

      {activeTab === 'reports' ? (
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search reports..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
              />
            </div>
          </div>

          <AdvancedFilters 
            currentFilters={filters as any} 
            onApply={(f) => setFilters(f as any)} 
            clients={clients}
            technicians={technicians}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredReports.map((report) => {
              const task = tasks.find(t => t.id === report.taskId);
              return (
                <motion.div
                  key={report.id}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="group bg-white border border-slate-100 rounded-3xl p-6 hover:shadow-xl hover:shadow-slate-100 transition-all flex flex-col"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex flex-col gap-1">
                      <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800 group-hover:bg-blue-800 group-hover:text-white transition-colors">
                        <FileText className="w-6 h-6" />
                      </div>
                      <span className="text-[10px] font-black text-orange-500 uppercase tracking-widest mt-1">{report.type}</span>
                    </div>
                    <div className="flex gap-1">
                      <button 
                        onClick={() => handleDownloadPdfFromPrintableView(report)}
                        className="p-2 text-slate-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-all cursor-pointer"
                        title="Download Formatted PDF"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => openViewModal(report)}
                        className="p-2 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all cursor-pointer"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {(isManager || report.technicianId === profile?.uid) && (report.status === 'draft' || report.status === 'rejected') && (
                        <button onClick={() => openModal(report)} className="p-2 text-slate-400 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all cursor-pointer">
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {(isAdmin || isManager) && (
                        <button onClick={() => handleDelete(report.id)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all cursor-pointer">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-lg font-bold text-slate-900 line-clamp-1">{task?.title || 'Unknown Task'}</h3>
                      {getStatusBadge(report.status)}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Building2 className="w-3.5 h-3.5" />
                      <span>{report.clientName}</span>
                    </div>
                  </div>

                  <div className="p-3 bg-slate-50 rounded-xl mb-4 flex-1">
                    <p className="text-xs text-slate-600 line-clamp-3">
                      <span className="font-bold">Findings:</span> {report.findings || 'N/A'}
                    </p>
                  </div>

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-50">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-slate-200 rounded-full overflow-hidden">
                        <img src={`https://ui-avatars.com/api/?name=${report.technicianName}&background=random`} alt="" />
                      </div>
                      <span className="text-[10px] font-medium text-slate-600">{report.technicianName}</span>
                    </div>
                    
                    {(isManager || isAdmin) && report.status === 'submitted_to_technical' && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleStatusChange(report, 'approved')}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                          title="Approve"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleStatusChange(report, 'rejected')}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Reject"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    {(isBilling || isManager || isAdmin) && report.status === 'approved' && (
                      <button
                        onClick={() => handleStatusChange(report, 'submitted_to_billing')}
                        className="p-1.5 text-orange-600 hover:bg-orange-50 rounded-lg"
                        title="Submit to Billing"
                      >
                        <Receipt className="w-4 h-4" />
                      </button>
                    )}
                    {report.status === 'approved' && report.type === 'survey' && (
                      <Link
                        to={`/invoices?reportId=${report.id}`}
                        className="p-1.5 text-blue-800 hover:bg-blue-50 rounded-lg"
                        title="Convert to Proforma"
                      >
                        <Receipt className="w-4 h-4" />
                      </Link>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
          {hasMoreReports && filteredReports.length >= reportLimit && (
            <div className="flex justify-center mt-6 pt-4 border-t border-slate-100">
              <button
                onClick={() => setReportLimit(prev => prev + 25)}
                className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-2xl font-bold text-sm transition-all shadow-sm flex items-center gap-2 cursor-pointer"
              >
                <span>Load More Reports ({reportLimit} currently loaded)</span>
              </button>
            </div>
          )}
        </div>
      ) : activeTab === 'contract_expiry' ? (
        <ContractExpiryReport clients={clients} />
      ) : activeTab === 'accounting' ? (
        <AccountingReports accounts={accounts} invoices={invoices} />
      ) : activeTab === 'client_billing' ? (
        <ClientBillingReport clients={clients} />
      ) : activeTab === 'procurement_history' ? (
        <ProcurementHistoryReport lpos={procurementLpos} requisitions={procurementRequisitions} />
      ) : (
        <UserTaskReportsTab 
          allUsers={allUsers}
          profile={profile}
          setConfirmModal={setConfirmModal}
        />
      )}

      {/* Create/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-slate-900">
                  {editingReport ? 'Edit Report' : 'Create New Report'}
                </h2>
                <button onClick={closeModal} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                  <X className="w-6 h-6 text-slate-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Select Task *</label>
                    <select
                      required
                      value={formData.taskId}
                      onChange={(e) => setFormData({ ...formData, taskId: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                    >
                      <option value="">Select a task</option>
                      {tasks.map(task => (
                        <option key={task.id} value={task.id}>
                          {task.clientName} - {task.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  {formData.clientId && clientBranches.length > 0 && (
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-slate-700">Branch *</label>
                      <select
                        required
                        value={formData.branchId}
                        onChange={(e) => setFormData({ ...formData, branchId: e.target.value })}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                      >
                        <option value="">Select a branch</option>
                        {clientBranches.map(branch => (
                          <option key={branch.id} value={branch.id}>{branch.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Report Type *</label>
                    <select
                      required
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as ReportType })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                    >
                      <option value="general">General Report</option>
                      <option value="inspection">Inspection Report</option>
                      <option value="survey">Technical Survey</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">Equipment Details</label>
                    <button 
                      type="button" 
                      onClick={addEquipmentRow}
                      className="text-xs font-bold text-blue-800 flex items-center gap-1 hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add Equipment
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.equipment.map((row, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-4 bg-slate-50 rounded-2xl relative group">
                        <input
                          placeholder="Name"
                          value={row.name}
                          onChange={(e) => updateEquipmentRow(index, 'name', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <input
                          placeholder="Model"
                          value={row.model}
                          onChange={(e) => updateEquipmentRow(index, 'model', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <input
                          placeholder="Serial #"
                          value={row.serialNumber}
                          onChange={(e) => updateEquipmentRow(index, 'serialNumber', e.target.value)}
                          className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                        />
                        <div className="flex gap-2">
                          <input
                            placeholder="Status"
                            value={row.status}
                            onChange={(e) => updateEquipmentRow(index, 'status', e.target.value)}
                            className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                          />
                          <button 
                            type="button" 
                            onClick={() => removeEquipmentRow(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {formData.equipment.length === 0 && (
                      <div className="text-center py-8 border-2 border-dashed border-slate-100 rounded-2xl text-slate-400 text-sm">
                        No equipment added yet.
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-semibold text-slate-700">Required Spare Parts</label>
                    <button 
                      type="button" 
                      onClick={() => setIsPartSelectorOpen(true)}
                      className="text-xs font-bold text-blue-800 flex items-center gap-1 hover:underline"
                    >
                      <Plus className="w-3 h-3" /> Add Part
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    {formData.requiredSpareParts.map((part, index) => (
                      <div key={index} className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl">
                        <div className="flex-1 flex items-center justify-between">
                          <p className="text-sm font-bold text-slate-900">{part.name}</p>
                          {part.unit && <span className="text-[10px] font-bold text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded ml-2">{part.unit}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min="1"
                            value={part.quantity}
                            onChange={(e) => {
                              const newParts = [...formData.requiredSpareParts];
                              newParts[index].quantity = parseInt(e.target.value) || 1;
                              setFormData({ ...formData, requiredSpareParts: newParts });
                            }}
                            className="w-20 px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm"
                          />
                          <button 
                            type="button" 
                            onClick={() => {
                              const newParts = [...formData.requiredSpareParts];
                              newParts.splice(index, 1);
                              setFormData({ ...formData, requiredSpareParts: newParts });
                            }}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                    {formData.requiredSpareParts.length === 0 && (
                      <div className="text-center py-4 border-2 border-dashed border-slate-100 rounded-2xl text-slate-400 text-sm">
                        No spare parts added.
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Labour Charge (Optional)</label>
                  <input
                    type="number"
                    value={formData.labourCharge}
                    onChange={(e) => setFormData({ ...formData, labourCharge: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all"
                    placeholder="Enter estimated labour charge"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Executive Summary *</label>
                  <textarea
                    required
                    rows={3}
                    value={formData.executiveSummary}
                    onChange={(e) => setFormData({ ...formData, executiveSummary: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                    placeholder="Provide a concise high-level summary highlighting key maintenance findings and recommendations..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Findings *</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.findings}
                      onChange={(e) => setFormData({ ...formData, findings: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                      placeholder="What did you find during the survey?"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-700">Recommendations *</label>
                    <textarea
                      required
                      rows={4}
                      value={formData.recommendations}
                      onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                      placeholder="What do you recommend based on your findings?"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">Additional Comments</label>
                  <textarea
                    rows={3}
                    value={formData.comments}
                    onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all resize-none"
                    placeholder="Any other notes or observations..."
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-6 py-3 border border-slate-200 rounded-2xl font-semibold text-slate-600 hover:bg-slate-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-6 py-3 bg-blue-800 text-white rounded-2xl font-bold hover:bg-blue-900 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 flex items-center justify-center gap-2 cursor-pointer"
                  >
                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingReport ? 'Save Changes' : 'Submit Report')}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Part Selector Modal */}
      <AnimatePresence>
        {isPartSelectorOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsPartSelectorOpen(false)}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-slate-900">Select Spare Parts</h3>
                <button onClick={() => setIsPartSelectorOpen(false)} className="p-2 hover:bg-slate-100 rounded-xl">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search parts..."
                  value={partSearchTerm}
                  onChange={(e) => setPartSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500/10"
                />
              </div>

              <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                {clientSpareParts
                  .filter(p => p.name.toLowerCase().includes(partSearchTerm.toLowerCase()))
                  .map((part, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        const existing = formData.requiredSpareParts.find(p => p.name === part.name);
                        if (existing) {
                          toast.error('Part already added');
                          return;
                        }
                        setFormData({
                          ...formData,
                          requiredSpareParts: [...formData.requiredSpareParts, { name: part.name, quantity: 1, unit: part.unit }]
                        });
                        setIsPartSelectorOpen(false);
                        toast.success('Part added');
                      }}
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-blue-50 rounded-2xl transition-all group"
                    >
                      <span className="font-bold text-slate-700 group-hover:text-blue-800">{part.name}</span>
                      <Plus className="w-4 h-4 text-slate-400 group-hover:text-blue-800" />
                    </button>
                  ))}
                
                {partSearchTerm && !clientSpareParts.find(p => p.name.toLowerCase() === partSearchTerm.toLowerCase()) && (
                  <button
                    onClick={() => {
                      setFormData({
                        ...formData,
                        requiredSpareParts: [...formData.requiredSpareParts, { name: partSearchTerm, quantity: 1, unit: 'pc' }]
                      });
                      setIsPartSelectorOpen(false);
                      toast.success('New part added');
                    }}
                    className="w-full flex items-center justify-between p-4 bg-blue-50 border border-dashed border-blue-200 rounded-2xl text-blue-800 font-bold"
                  >
                    <span>Add "{partSearchTerm}" as new part</span>
                    <Plus className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* View Modal */}
      <AnimatePresence>
        {isViewModalOpen && selectedReport && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeViewModal}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="relative w-full max-w-4xl bg-white rounded-3xl shadow-2xl p-8 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-slate-900">Report Details</h2>
                    <p className="text-slate-500 text-sm uppercase font-bold tracking-widest">{selectedReport.type}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button 
                    onClick={() => handleDownloadPdfFromPrintableView(selectedReport)}
                    className="flex items-center gap-1.5 px-3.5 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                    title="Download Formatted PDF from Printable View"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download PDF</span>
                  </button>
                  <button 
                    onClick={handlePrintSingleReport}
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all"
                    title="Print Report"
                  >
                    <Printer className="w-6 h-6" />
                  </button>
                  <button onClick={closeViewModal} className="p-2 hover:bg-slate-100 rounded-xl transition-colors">
                    <X className="w-6 h-6 text-slate-400" />
                  </button>
                </div>
              </div>

              {/* Print Preferences Quick Bar */}
              <div className="flex flex-wrap gap-4 items-center justify-between p-4 bg-blue-50/50 border border-blue-100 rounded-2xl mb-6">
                <div className="flex items-center gap-2 text-xs font-bold text-blue-900">
                  <Printer className="w-4 h-4 text-blue-800" />
                  <span>Report Print Configuration:</span>
                </div>
                <div className="flex flex-wrap gap-4 items-center">
                  {/* Orientation Selection */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] text-slate-500 font-medium">Layout:</span>
                    <select
                      value={printPreferences.printOrientation}
                      onChange={(e) => setPrintPreferences(prev => ({ ...prev, printOrientation: e.target.value as 'portrait' | 'landscape' }))}
                      className="bg-white border border-slate-200 text-slate-700 text-[11px] font-bold rounded-lg px-2.5 py-1 focus:outline-none"
                    >
                      <option value="portrait">Portrait</option>
                      <option value="landscape">Landscape</option>
                    </select>
                  </div>

                  {/* Header Visibility Toggle */}
                  <label className="flex items-center gap-1.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={printPreferences.printShowHeader}
                      onChange={(e) => setPrintPreferences(prev => ({ ...prev, printShowHeader: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-blue-800 focus:ring-blue-800"
                    />
                    <span className="text-[11px] text-slate-500 font-bold">Include Header Letterhead</span>
                  </label>

                  {/* Save defaults button */}
                  <button
                    type="button"
                    onClick={async () => {
                      const userId = user?.uid || profile?.uid;
                      if (!userId) return;
                      try {
                        const { doc, setDoc } = await import('firebase/firestore');
                        const docRef = doc(db, 'userNotificationSettings', userId);
                        await setDoc(docRef, {
                          printOrientation: printPreferences.printOrientation,
                          printShowHeader: printPreferences.printShowHeader,
                        }, { merge: true });
                        toast.success('Preferences saved as your system default!');
                      } catch (err) {
                        console.error(err);
                        toast.error('Failed to save default preferences');
                      }
                    }}
                    className="bg-blue-800 hover:bg-blue-950 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-lg transition"
                  >
                    Save As Default
                  </button>
                </div>
              </div>

              <div className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="p-4 bg-slate-50 rounded-2xl">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Client</p>
                    <p className="font-bold text-slate-900">{selectedReport.clientName}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Technician</p>
                    <p className="font-bold text-slate-900">{selectedReport.technicianName}</p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-2xl">
                    <p className="text-xs font-bold text-slate-400 uppercase mb-1">Status</p>
                    {getStatusBadge(selectedReport.status)}
                  </div>
                </div>

                <div className="space-y-3 bg-gradient-to-r from-blue-50/50 to-indigo-50/20 p-6 rounded-2.5xl border border-blue-100/40">
                  <h3 className="font-bold text-blue-950 flex items-center gap-2 text-sm uppercase tracking-wider">
                    <FileText className="w-5 h-5 text-blue-800" />
                    Executive Summary
                  </h3>
                  <div className="text-slate-800 leading-relaxed font-semibold text-sm whitespace-pre-wrap">
                    {selectedReport.executiveSummary || 'No executive summary provided for this report.'}
                  </div>
                </div>

                {selectedReport.equipment && selectedReport.equipment.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-blue-800" />
                      Equipment Details
                    </h3>
                    <div className="overflow-hidden border border-slate-100 rounded-2xl">
                      <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 text-[10px] font-bold text-slate-400 uppercase">
                          <tr>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">Model</th>
                            <th className="px-4 py-3">Serial #</th>
                            <th className="px-4 py-3">Status</th>
                          </tr>
                        </thead>
                        <tbody className="text-sm divide-y divide-slate-50">
                          {selectedReport.equipment.map((row, i) => (
                            <tr key={i}>
                              <td className="px-4 py-3 font-medium">{row.name}</td>
                              <td className="px-4 py-3 text-slate-600">{row.model}</td>
                              <td className="px-4 py-3 text-slate-600">{row.serialNumber}</td>
                              <td className="px-4 py-3">
                                <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold">{row.status}</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900">Findings</h3>
                    <div className="p-4 bg-slate-50 rounded-2xl text-sm text-slate-600 leading-relaxed italic">
                      "{selectedReport.findings || 'No findings reported.'}"
                    </div>
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900">Recommendations</h3>
                    <div className="p-4 bg-slate-50 rounded-2xl text-sm text-slate-600 leading-relaxed italic">
                      "{selectedReport.recommendations || 'No recommendations provided.'}"
                    </div>
                  </div>
                </div>

                {selectedReport.comments && (
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900">Additional Comments</h3>
                    <div className="p-4 bg-slate-50 rounded-2xl text-sm text-slate-600 leading-relaxed italic">
                      "{selectedReport.comments}"
                    </div>
                  </div>
                )}

                {selectedReport.requiredSpareParts && selectedReport.requiredSpareParts.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="font-bold text-slate-900 flex items-center gap-2">
                      <Zap className="w-5 h-5 text-blue-800" />
                      Required Spare Parts
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedReport.requiredSpareParts.map((part, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                          <span className="text-sm font-medium text-slate-700">{part.name}</span>
                          <span className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-blue-800">x{part.quantity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(selectedReport.labourCharge || 0) > 0 && (
                  <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between">
                    <span className="text-sm font-bold text-blue-800 uppercase tracking-wider">Estimated Labour Charge</span>
                    <span className="text-xl font-black text-blue-900">{(selectedReport.labourCharge || 0).toLocaleString()}</span>
                  </div>
                )}

                {selectedReport.managerName && (
                  <div className="pt-6 border-t border-slate-50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center text-emerald-600">
                        <CheckCircle2 className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase">Approved By</p>
                        <p className="font-bold text-slate-900">{selectedReport.managerName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-bold text-slate-400 uppercase">Approval Date</p>
                      <p className="font-bold text-slate-900">{selectedReport.approvalDate?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPartSelectorOpen && (
          <div className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-black/50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white p-8 rounded-[2.5rem] w-full max-w-md relative shadow-2xl"
            >
              <button 
                onClick={() => setIsPartSelectorOpen(false)}
                className="absolute top-6 right-6 p-2 hover:bg-slate-100 rounded-xl transition-colors text-slate-400"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-800">
                  <Zap className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-black text-slate-900">Select Spare Part</h2>
                  <p className="text-sm text-slate-500 font-medium italic">Choose from client's spare parts list</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search or type new part..."
                    value={partSearchTerm}
                    onChange={(e) => setPartSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all"
                  />
                </div>

                <div className="max-h-60 overflow-y-auto space-y-2 pr-2 custom-scrollbar">
                  {clientSpareParts
                    .filter(p => p.name.toLowerCase().includes(partSearchTerm.toLowerCase()))
                    .map((part, i) => (
                      <button
                        key={i}
                        onClick={() => addSparePart(part)}
                        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-blue-50 hover:text-blue-800 rounded-2xl transition-all group"
                      >
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-bold">{part.name}</span>
                          {part.unit && (
                            <span className="text-[10px] font-black text-blue-600/60 uppercase tracking-widest bg-blue-100/50 px-2 py-0.5 rounded-md">
                              Unit: {part.unit}
                            </span>
                          )}
                        </div>
                        <Plus className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </button>
                    ))}
                  
                  {partSearchTerm && !clientSpareParts.find(p => p.name.toLowerCase() === partSearchTerm.toLowerCase()) && (
                    <button
                      onClick={() => addSparePart({ name: partSearchTerm, isNew: true })}
                      className="w-full flex items-center justify-between p-4 bg-blue-50 text-blue-800 rounded-2xl border border-dashed border-blue-200 hover:bg-blue-100 transition-all"
                    >
                      <div className="flex flex-col items-start">
                        <span className="font-bold">Add "{partSearchTerm}"</span>
                        <span className="text-[10px] uppercase tracking-wider font-black opacity-60">New Spare Part</span>
                      </div>
                      <Plus className="w-5 h-5" />
                    </button>
                  )}

                  {clientSpareParts.length === 0 && !partSearchTerm && (
                    <div className="text-center py-8 text-slate-400 text-sm italic">
                      No spare parts found for this client.
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="YES"
        cancelLabel="NO"
        isDanger={true}
      />
      {createPortal(
        selectedReport && (
          <div id="printable-report" className="print-only single-report-print-target p-8 bg-white text-black font-sans leading-relaxed">
            {/* Print Header/Letterhead */}
            {printPreferences.printShowHeader ? (
              <div className="border-b-4 border-slate-900 pb-6 mb-8 flex flex-row justify-between items-start">
                <div>
                  <h1 className="text-3xl font-black text-blue-900 tracking-tight mb-1 uppercase">
                    Bright Technical Systems
                  </h1>
                  <p className="text-xs text-slate-600 font-bold uppercase tracking-wider max-w-md">
                    Specialists in Industrial & Technical Solutions, Maintenance, Inspection and Calibration Services
                  </p>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Contact: service@brighttechnical.com | Office Ref: BTS-REP-{(selectedReport.id || '').substring(0, 6).toUpperCase()}
                  </p>
                </div>
                <div className="text-right border-r-4 border-blue-900 pr-4 py-1">
                  <h2 className="text-xl font-black text-slate-800 tracking-tight uppercase">
                    Technical Report
                  </h2>
                  <p className="text-xs font-black text-blue-800 bg-blue-50 px-2.5 py-1 rounded inline-block mt-1 uppercase tracking-wider">
                    {selectedReport.type} REPORT
                  </p>
                </div>
              </div>
            ) : (
              <div className="border-b-2 border-slate-900 pb-4 mb-6 flex flex-row justify-between items-center text-xs">
                <span className="font-bold text-slate-700">Bright Technical Systems — Office Ref: BTS-REP-{(selectedReport.id || '').substring(0, 6).toUpperCase()}</span>
                <span className="font-bold text-slate-900 uppercase tracking-wider">{selectedReport.type} REPORT</span>
              </div>
            )}

            {/* Metadata Block (Two Columns) */}
            <div className="grid grid-cols-2 gap-6 pb-6 mb-8 border-b border-slate-200 text-xs">
              <div className="space-y-2">
                <div>
                  <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">Client / Account</span>
                  <span className="font-bold text-slate-900 text-sm">{selectedReport.clientName}</span>
                  {selectedReport.branchName && (
                    <span className="text-slate-500 block text-[11px]">Branch: {selectedReport.branchName}</span>
                  )}
                </div>
                <div>
                  <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">Job Reference Task ID</span>
                  <span className="font-mono text-slate-700">{selectedReport.taskId || 'Direct Service Record'}</span>
                </div>
              </div>
              <div className="space-y-2 text-right">
                <div>
                  <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">Service Technician</span>
                  <span className="font-bold text-slate-900 text-sm">{selectedReport.technicianName}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 justify-end">
                  <div>
                    <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">Status</span>
                    <span className="font-bold text-slate-800 uppercase text-[11px]">{selectedReport.status.replace(/_/g, ' ')}</span>
                  </div>
                  <div>
                    <span className="font-bold text-slate-400 uppercase tracking-wider block text-[10px]">Report Date</span>
                    <span className="font-bold text-slate-800 text-[11px]">
                      {selectedReport.createdAt ? selectedReport.createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dedicated Executive Summary Section */}
            <div className="mb-8 p-5 bg-blue-50/50 rounded-xl border border-blue-200 text-xs">
              <h3 className="text-[10px] font-black uppercase text-blue-900 tracking-wider border-b border-blue-200 pb-1 mb-2">
                Executive Summary (Key Maintenance Findings & Recommendations)
              </h3>
              <p className="text-slate-800 leading-relaxed whitespace-pre-wrap font-semibold">
                {selectedReport.executiveSummary || 'No executive summary provided for this technical report.'}
              </p>
            </div>

            {/* Equipment Details Table Section */}
            {selectedReport.equipment && selectedReport.equipment.length > 0 && (
              <div className="mb-8 space-y-2">
                <h3 className="text-sm font-black uppercase text-slate-800 tracking-wider border-b border-slate-300 pb-1">
                  Inspected / Serviced Equipment Registry
                </h3>
                <table className="w-full text-left border-collapse text-xs border border-slate-300">
                  <thead>
                    <tr className="bg-slate-100 font-bold border-b border-slate-300 text-slate-700">
                      <th className="p-3 border-r border-slate-300">#</th>
                      <th className="p-3 border-r border-slate-300">Equipment Name</th>
                      <th className="p-3 border-r border-slate-300">Model Name / Number</th>
                      <th className="p-3 border-r border-slate-300">Serial Number</th>
                      <th className="p-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {selectedReport.equipment.map((eq, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="p-3 border-r border-slate-300 font-bold text-slate-500">{idx + 1}</td>
                        <td className="p-3 border-r border-slate-300 font-bold text-slate-900">{eq.name}</td>
                        <td className="p-3 border-r border-slate-300 text-slate-700">{eq.model || 'N/A'}</td>
                        <td className="p-3 border-r border-slate-300 text-slate-700 font-mono">{eq.serialNumber || 'N/A'}</td>
                        <td className="p-3">
                          <span className="font-bold text-blue-900 uppercase text-[10px]">{eq.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Core Content Box (Findings & Recommendations) */}
            <div className="grid grid-cols-2 gap-8 mb-8 text-xs">
              <div className="space-y-2 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h3 className="text-sm font-black uppercase text-slate-800 tracking-wider border-b border-slate-200 pb-1">
                  Diagnostic Findings
                </h3>
                <p className="text-slate-800 leading-relaxed whitespace-pre-wrap italic">
                  "{selectedReport.findings || 'No findings recorded during this inspection.'}"
                </p>
              </div>
              <div className="space-y-2 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <h3 className="text-sm font-black uppercase text-slate-800 tracking-wider border-b border-slate-200 pb-1">
                  Actionable Recommendations
                </h3>
                <p className="text-slate-800 leading-relaxed whitespace-pre-wrap italic">
                  "{selectedReport.recommendations || 'No recommendations provided for future action.'}"
                </p>
              </div>
            </div>

            {/* Additional Comments (if any) */}
            {selectedReport.comments && (
              <div className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-200 text-xs space-y-2">
                <h3 className="text-sm font-black uppercase text-slate-800 tracking-wider border-b border-slate-200 pb-1">
                  Additional Comments
                </h3>
                <p className="text-slate-800 leading-relaxed whitespace-pre-wrap italic">
                  "{selectedReport.comments}"
                </p>
              </div>
            )}

            {/* Required Spare Parts Table Section */}
            {selectedReport.requiredSpareParts && selectedReport.requiredSpareParts.length > 0 && (
              <div className="mb-8 space-y-2">
                <h3 className="text-sm font-black uppercase text-slate-800 tracking-wider border-b border-slate-300 pb-1">
                  Required Spare Parts List
                </h3>
                <table className="w-full text-left border-collapse text-xs border border-slate-300">
                  <thead>
                    <tr className="bg-slate-100 font-bold border-b border-slate-300 text-slate-700">
                      <th className="p-3 border-r border-slate-300">Part Name / Description</th>
                      <th className="p-3 border-r border-slate-300">Quantity Required</th>
                      <th className="p-3 border-r border-slate-300">Unit Price</th>
                      <th className="p-3">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {selectedReport.requiredSpareParts.map((part, idx) => (
                      <tr key={idx}>
                        <td className="p-3 border-r border-slate-300 font-bold text-slate-900">{part.name}</td>
                        <td className="p-3 border-r border-slate-300 font-mono text-center">x{part.quantity}</td>
                        <td className="p-3 border-r border-slate-300 font-mono text-right">
                          {part.price !== undefined ? `${part.price.toLocaleString()} UGX` : 'N/A'}
                        </td>
                        <td className="p-3 font-mono text-right">
                          {part.price !== undefined ? `${(part.price * part.quantity).toLocaleString()} UGX` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Labour Charge & Financial Summary */}
            {((selectedReport.labourCharge || 0) > 0 || (selectedReport.requiredSpareParts && selectedReport.requiredSpareParts.length > 0)) && (
              <div className="mb-8 p-4 bg-slate-50 rounded-xl border border-slate-200 flex flex-col items-end text-xs space-y-1">
                {selectedReport.labourCharge !== undefined && (
                  <div className="flex justify-between w-64 border-b border-slate-200 pb-1">
                    <span className="text-slate-500 font-bold">Estimated Labour Charge:</span>
                    <span className="font-mono font-bold">{selectedReport.labourCharge.toLocaleString()} UGX</span>
                  </div>
                )}
                {selectedReport.requiredSpareParts && selectedReport.requiredSpareParts.length > 0 && (
                  <div className="flex justify-between w-64 border-b border-slate-200 pb-1">
                    <span className="text-slate-500 font-bold">Total Parts Cost:</span>
                    <span className="font-mono font-bold">
                      {selectedReport.requiredSpareParts.reduce((sum, part) => sum + ((part.price || 0) * part.quantity), 0).toLocaleString()} UGX
                    </span>
                  </div>
                )}
                <div className="flex justify-between w-64 pt-1">
                  <span className="text-slate-900 font-black uppercase text-xs">Total Estimated Cost:</span>
                  <span className="font-mono font-black text-blue-900 text-sm">
                    {((selectedReport.labourCharge || 0) + 
                      (selectedReport.requiredSpareParts || []).reduce((sum, part) => sum + ((part.price || 0) * part.quantity), 0)
                    ).toLocaleString()} UGX
                  </span>
                </div>
              </div>
            )}

            {/* Physical Storage Verification & Signatures Footer */}
            <div className="mt-16 pt-12 border-t border-dashed border-slate-300 grid grid-cols-2 gap-12 text-xs">
              <div className="space-y-8">
                <p className="font-bold text-slate-800">PREPARED & SUBMITTED BY:</p>
                <div className="space-y-1">
                  <div className="border-b border-slate-400 w-full h-8" />
                  <p className="text-[10px] text-slate-500 font-bold">Service Engineer Signature</p>
                  <p className="font-bold text-slate-900">{selectedReport.technicianName}</p>
                  <p className="text-[9px] text-slate-400 font-mono">Date Signed: ____ / ____ / ________</p>
                </div>
              </div>
              <div className="space-y-8 text-right flex flex-col items-end">
                <p className="font-bold text-slate-800 text-left w-full max-w-xs">VERIFIED & APPROVED BY:</p>
                <div className="space-y-1 w-full max-w-xs text-left">
                  <div className="border-b border-slate-400 w-full h-8" />
                  <p className="text-[10px] text-slate-500 font-bold">Manager Approval Signature</p>
                  <p className="font-bold text-slate-900">{selectedReport.managerName || '_________________________'}</p>
                  <p className="text-[9px] text-slate-400 font-mono">Date Approved: {selectedReport.approvalDate ? selectedReport.approvalDate.toDate().toLocaleDateString() : '____ / ____ / ________'}</p>
                </div>
              </div>
            </div>
          </div>
        ),
        document.body
      )}
    </div>
  );
};

export default ReportsPage;
